/**
 * EndGameCountdown - shows countdown when game is ending
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../../stores/gameStore';

export function EndGameCountdown() {
  const endGameTimer = useGameStore((s) => s.endGameTimer);
  // Freeze while paused / resuming - GAME_RESUMED carries the shifted deadline
  const isFrozen = useGameStore((s) => (s.gameState?.isPaused ?? false) || s.resumeCountdownEndsAt !== null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!endGameTimer) {
      setSecondsLeft(null);
      return;
    }
    if (isFrozen) {
      setSecondsLeft((prev) => prev ?? Math.max(0, Math.ceil((endGameTimer - Date.now()) / 1000)));
      return;
    }

    const updateSeconds = () => {
      const remaining = Math.max(0, Math.ceil((endGameTimer - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };

    updateSeconds();
    const interval = setInterval(updateSeconds, 1000);

    return () => clearInterval(interval);
  }, [endGameTimer, isFrozen]);

  if (secondsLeft === null) return null;

  const isExpired = secondsLeft === 0;
  const isUrgent = secondsLeft <= 30;
  const isCritical = secondsLeft <= 10;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        className={`end-countdown fixed z-40 px-3 py-1.5 rounded-lg shadow-lg text-sm ${
          isCritical
            ? 'bg-red-500 text-white'
            : isUrgent
            ? 'bg-amber-500 text-white'
            : 'bg-slate-700/90 text-white'
        }`}
      >
        <div className="flex items-center gap-2">
          <motion.div
            animate={isCritical ? { scale: [1, 1.2, 1] } : undefined}
            transition={isCritical ? { duration: 0.5, repeat: Infinity } : undefined}
          >
            {isCritical ? '🔥' : isUrgent ? '⏰' : '⌛'}
          </motion.div>
          <div className="font-mono font-bold">
            {isExpired ? 'NOW' : formatCountdown(secondsLeft)}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function formatCountdown(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
